import React from "react"
import ColumnGroup from "../../../../../components/atoms/table/ColumnGroup.atom"
import Row from "../../../../../components/atoms/table/RowColumnGroup.atom"
import Column from "../../../../../components/atoms/table/Column.atom"
import {
  floatToPercentage,
  formatDecimals,
  numberFormatter,
} from "../../../../../utils/numbers.utils"
import { TimeDifferenceBadge } from "../../components/CellReportBadges"
import { formatPercentageSuffix } from "../../../../../utils/strings.utils"
import { BREAKPOINTS } from "../../../../../consts/responsiveBreakpoints.consts"
import useScreenDimensionsContext from "../../../../../hooks/contextConsumers/useScreenDimensionsContext.hook"

const footerColumnGroup = (
  WEEK_SIZE,
  reportData,
  recalculateFooter,
  filteredUsers,
  horizontalScrollCursor
) => {
  const [footerData, setFooterData] = React.useState()
  const { screenWidth } = useScreenDimensionsContext()
  React.useEffect(() => {
    calculateTotals()
  }, [reportData, recalculateFooter, filteredUsers])

  const calculateTotals = () => {
    reportData.teamMemberReport.forEach((member) => {
      member.weekSisuTime = Math.abs(member.weekSisuTime)
      member.weekBilledTime = Math.abs(member.weekBilledTime)
      member.weekClientTime = Math.abs(member.weekClientTime)
      member.weekClientBilledTime = Math.abs(member.weekClientBilledTime)
      member.weekTimeDifference = Math.abs(member.weekTimeDifference)
    })

    const totalReport = reportData.teamMemberReport.reduce(
      (acc, current) => {
        if (
          filteredUsers?.length > 0 &&
          !filteredUsers.includes(current.teamMemberTagName)
        ) {
          return acc
        }
        acc.ocupationRatioAverage += parseFloat(current.ocupationRatio)
        acc.weekSisuTime += parseFloat(current.weekSisuTime)
        acc.weekBilledTime += parseFloat(current.weekBilledTime)
        acc.weekClientTime += parseFloat(current.weekClientTime)
        acc.weekClientBilledTime += parseFloat(current.weekClientBilledTime)
        acc.weekTimeDifference += parseFloat(current.weekTimeDifference)
        return acc
      },
      {
        ocupationRatioAverage: 0,
        weekSisuTime: 0,
        weekBilledTime: 0,
        weekClientTime: 0,
        weekClientBilledTime: 0,
        weekTimeDifference: 0,
        timeDifferenceComments: [],
      }
    )

    totalReport.ocupationRatioAverage =
      totalReport.ocupationRatioAverage / reportData.teamMemberReport.length
    totalReport.weekSisuTimeAverage = (
      totalReport.weekSisuTime / reportData.teamMemberReport.length
    ).toFixed(2)
    totalReport.weekBilledTimeAverage = (
      totalReport.weekBilledTime / reportData.teamMemberReport.length
    ).toFixed(2)
    totalReport.weekClientTimeAverage = (
      totalReport.weekClientTime / reportData.teamMemberReport.length
    ).toFixed(2)
    totalReport.weekClientBilledTimeAverage = (
      totalReport.weekClientBilledTime / reportData.teamMemberReport.length
    ).toFixed(2)
    totalReport.weekTimeDifferenceAverage = (
      totalReport.weekTimeDifference / reportData.teamMemberReport.length
    ).toFixed(2)
    setFooterData({ totalReport })
  }

  const isOnMobile = screenWidth <= BREAKPOINTS.MOBILE_BREAKPOINT

  return (
    footerData && (
      <ColumnGroup key={recalculateFooter}>
        <Row>
          <Column
            footer="Totales"
            frozen
            style={
              screenWidth > BREAKPOINTS.MOBILE_BREAKPOINT ?
                { minWidth: "236px", width: 236 }
              : { minWidth: "94px", paddingRight: 0, paddingLeft: 4 }
            }
          />
          {new Array(WEEK_SIZE).fill(null).map((_, index) => {
            return (
              <Column
                key={index}
                footer={""}
                style={{ minWidth: 103 }}
                hidden={isOnMobile}
              />
            )
          })}
          <Column
            hidden={isOnMobile && horizontalScrollCursor > 0}
            footer={numberFormatter(footerData.totalReport.weekSisuTime)}
            style={
              isOnMobile ?
                {
                  minWidth: "98px",
                  paddingLeft: 0,
                  paddingRight: 0,
                }
              : null
            }
          />
          <Column
            hidden={isOnMobile && horizontalScrollCursor > 1}
            footer={numberFormatter(
              footerData.totalReport.weekClientBilledTime
            )}
            style={
              isOnMobile ?
                {
                  minWidth: "98px",
                  paddingLeft: horizontalScrollCursor === 0 ? 3 : 0,
                }
              : null
            }
          />
          <Column
            hidden={isOnMobile && horizontalScrollCursor < 1}
            footer={() => (
              <div className="flex justify-content-between align-items-center w-full">
                <span className="w-4 text-left">
                  {numberFormatter(footerData.totalReport.weekTimeDifference) ||
                    "0"}
                </span>
                <span className="flex-grow-1 text-left">
                  {footerData.totalReport.weekTimeDifference !== 0 && (
                    <TimeDifferenceBadge
                      value={numberFormatter(
                        footerData.totalReport.weekTimeDifference
                      )}
                      minTreshold={0}
                      maxTreshold={0}
                      referenceValue={0}
                      percentage={formatPercentageSuffix(
                        footerData.totalReport.weekTimeDifference /
                          footerData.totalReport.weekClientTime,
                        1
                      )}
                    />
                  )}
                </span>
              </div>
            )}
            style={
              isOnMobile ?
                {
                  minWidth: "98px",
                  paddingLeft: horizontalScrollCursor === 1 ? 4 : 0,
                }
              : null
            }
          />
          <Column
            hidden={isOnMobile && horizontalScrollCursor < 2}
            footer={"-"}
            style={
              isOnMobile ?
                {
                  minWidth: "98px",
                  paddingLeft: 4,
                }
              : null
            }
          />
        </Row>
        <Row>
          <Column
            footer="Promedios"
            frozen
            style={
              screenWidth > BREAKPOINTS.MOBILE_BREAKPOINT ?
                { minWidth: "236px", width: 236 }
              : { minWidth: "94px", width: 94, paddingLeft: 4 }
            }
          />
          {new Array(WEEK_SIZE).fill(null).map((_, index) => {
            return (
              <Column
                key={index}
                footer={""}
                style={{ minWidth: 103 }}
                hidden={isOnMobile}
              />
            )
          })}
          <Column
            hidden={isOnMobile && horizontalScrollCursor > 0}
            footer={numberFormatter(footerData.totalReport.weekSisuTimeAverage)}
            style={
              isOnMobile ?
                {
                  minWidth: "98px",
                  paddingLeft: 0,
                }
              : null
            }
          />
          <Column
            hidden={isOnMobile && horizontalScrollCursor > 1}
            footer={numberFormatter(
              footerData.totalReport.weekClientBilledTimeAverage
            )}
            style={
              isOnMobile ?
                {
                  minWidth: "98px",
                  paddingLeft: horizontalScrollCursor === 1 ? 0 : 3,
                }
              : null
            }
          />
          <Column
            hidden={isOnMobile && horizontalScrollCursor < 1}
            footer={numberFormatter(
              footerData.totalReport.weekTimeDifferenceAverage,
              2
            )}
            style={
              isOnMobile ?
                {
                  minWidth: "98px",
                  paddingLeft: horizontalScrollCursor === 1 ? 4 : 0,
                }
              : null
            }
          />
          <Column
            hidden={isOnMobile && horizontalScrollCursor < 2}
            footer={formatPercentageSuffix(
              footerData.totalReport.ocupationRatioAverage
            )}
            style={
              isOnMobile ?
                {
                  minWidth: "98px",
                  paddingLeft: 4,
                }
              : null
            }
          />
        </Row>
      </ColumnGroup>
    )
  )
}

export default footerColumnGroup
